/**
 * ScriptBlockView — read-only renderer for a single ScriptBlock.
 * Single-clicking the text opens the block editor (I-beam cursor on hover);
 * an X icon on hover deletes. The block fills its fixed-height timeline slot
 * (see FormattedMotionPrompt); when the text does not fit, the body scrolls
 * vertically inside the block while the timestamp / kind header stays put.
 *
 * Round 4:
 *  - Body uses the shared RichTextBox in read-only (`disabled`) mode so
 *    `@character` mentions render as chips (item 7).
 *  - Empty dialogue/voiceover bodies no longer render the literal `""`
 *    placeholder (item 9). The serializer still wraps the body in quotes on
 *    write, but read-mode rendering only adds quotes when there is text.
 */

import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { ScriptBlock } from '../scriptParser'
import { TimestampEditor } from './TimestampEditor'
import { RichTextBox } from '../../shared/RichTextBox'
import type { ReactFlowCanvasNode } from '../../../../../hooks/useYCanvas'
import { createNadDebug } from '../../../../../utils/debugLog'

const avScriptLog = createNadDebug('dw:av-script-format')

interface ScriptBlockViewProps {
  block: ScriptBlock
  hasOverlap?: boolean
  onEdit: () => void
  onDelete: () => void
  onTimestampCommit: (startSec: number, endSec: number) => void
  /** Canvas nodes for the @mention chips. */
  canvasNodes?: ReactFlowCanvasNode[]
  /** Owning VideoGenerator node id — used for NAD trace correlation. */
  nodeId?: string
}

function formatTs(start: number, end: number): string {
  const f = (n: number) => `${(Math.round(n * 10) / 10).toFixed(1)}s`
  return `${f(start)}-${f(end)}`
}

export const ScriptBlockView = memo(function ScriptBlockView({
  block,
  hasOverlap = false,
  onEdit,
  onDelete,
  onTimestampCommit,
  canvasNodes,
  nodeId,
}: ScriptBlockViewProps) {
  const { t } = useTranslation('canvas')
  const [editingTs, setEditingTs] = useState(false)
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const popoverRef = useRef<HTMLDivElement>(null)
  const deleteButtonRef = useRef<HTMLButtonElement>(null)
  const overlapClass = hasOverlap ? 'border-red-500/60' : 'border-border-subtle'
  const kindLabel = block.kind === 'sfx' ? 'sound effect' : block.kind
  // Item 9: only wrap dialogue/voiceover body in quotes when text is non-empty.
  const trimmedText = block.text.trim()
  const isSpeech = block.kind === 'dialogue' || block.kind === 'voiceover'
  const displayText = isSpeech && trimmedText ? `“${block.text}”` : block.text

  // Round 5 bug 3: confirm popover for block deletion.
  const dismissConfirm = useCallback(
    (dismissedBy: 'cancel' | 'escape' | 'outside-click' | 'confirm') => {
      setConfirmingDelete(false)
      if (dismissedBy !== 'confirm') {
        avScriptLog({
          event: 'block-delete-cancelled',
          nodeId,
          blockId: block.id,
          kind: block.kind,
          dismissedBy,
        })
      }
    },
    [nodeId, block.id, block.kind],
  )

  const handleDeleteClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      setConfirmingDelete((prev) => {
        if (prev) return prev
        avScriptLog({
          event: 'block-delete-confirm-shown',
          nodeId,
          blockId: block.id,
          kind: block.kind,
        })
        return true
      })
    },
    [nodeId, block.id, block.kind],
  )

  const handleConfirmDelete = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      avScriptLog({
        event: 'block-delete-confirmed',
        nodeId,
        blockId: block.id,
        kind: block.kind,
      })
      dismissConfirm('confirm')
      onDelete()
    },
    [nodeId, block.id, block.kind, dismissConfirm, onDelete],
  )

  useEffect(() => {
    if (!confirmingDelete) return
    const onDocMouseDown = (ev: MouseEvent) => {
      const target = ev.target
      if (!(target instanceof Node)) return
      if (popoverRef.current?.contains(target)) return
      if (deleteButtonRef.current?.contains(target)) return
      dismissConfirm('outside-click')
    }
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') {
        ev.stopPropagation()
        dismissConfirm('escape')
      }
    }
    document.addEventListener('mousedown', onDocMouseDown, true)
    document.addEventListener('keydown', onKey, true)
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown, true)
      document.removeEventListener('keydown', onKey, true)
    }
  }, [confirmingDelete, dismissConfirm])

  return (
    <div
      className={`group relative flex flex-col bg-surface-1 rounded-lg p-2.5 border ${overlapClass} h-full w-full`}
      data-testid={`script-block-${block.kind}-${block.id}`}
    >
      <div className="flex items-center justify-between gap-2 shrink-0">
        <div className="flex items-center gap-1.5 min-w-0">
          {editingTs ? (
            <TimestampEditor
              blockId={block.id}
              startSec={block.startSec}
              endSec={block.endSec}
              onCommit={(s, e) => {
                onTimestampCommit(s, e)
                setEditingTs(false)
              }}
              onCancel={() => setEditingTs(false)}
            />
          ) : (
            // Single click opens the inline timestamp editor.
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation()
                setEditingTs(true)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.stopPropagation()
                  setEditingTs(true)
                }
              }}
              className="inline-block text-xxs px-1.5 py-0.5 rounded-full bg-primary/20 text-primary font-medium cursor-pointer select-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              data-testid={`block-ts-${block.id}`}
              title={t('scriptBlockView.clickToEdit')}
            >
              {formatTs(block.startSec, block.endSec)}
            </span>
          )}
          <span className="text-xxs uppercase tracking-wider text-primary">
            {kindLabel}
          </span>
        </div>
        <div
          className={`flex items-center gap-0.5 transition-opacity ${
            confirmingDelete ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
          }`}
        >
          <button
            ref={deleteButtonRef}
            type="button"
            onClick={handleDeleteClick}
            title={t('scriptBlockView.deleteBlock')}
            className={`w-5 h-5 rounded hover:bg-destructive/20 text-text-muted hover:text-destructive flex items-center justify-center ${
              confirmingDelete ? 'bg-destructive/20 text-destructive opacity-100' : ''
            }`}
            data-testid={`block-delete-${block.id}`}
            aria-haspopup="dialog"
            aria-expanded={confirmingDelete}
          >
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
          {confirmingDelete && (
            <div
              ref={popoverRef}
              role="dialog"
              aria-label={t('scriptBlockView.deleteThisBlock')}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              data-testid={`block-delete-confirm-${block.id}`}
              className="absolute right-1 top-7 z-30 rounded-md border border-border bg-surface-2 shadow-lg px-2.5 py-2 text-xs font-medium flex flex-col gap-1.5 min-w-[148px]"
            >
              <div className="text-foreground/90">{t('scriptBlockView.deleteThisBlock')}</div>
              <div className="flex items-center justify-end gap-1.5">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    dismissConfirm('cancel')
                  }}
                  className="px-2 py-0.5 rounded text-text-muted hover:text-foreground hover:bg-surface-1"
                  data-testid={`block-delete-cancel-${block.id}`}
                >
                  {t('scriptBlockView.cancel')}
                </button>
                <button
                  type="button"
                  onClick={handleConfirmDelete}
                  className="px-2 py-0.5 rounded bg-destructive/80 text-destructive-foreground hover:bg-destructive"
                  data-testid={`block-delete-confirm-btn-${block.id}`}
                >
                  {t('scriptBlockView.delete')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Single-click anywhere on the text to edit the block. */}
      <div
        role="button"
        tabIndex={0}
        onClick={onEdit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onEdit()
        }}
        title={t('scriptBlockView.editBlock')}
        // Scrolls inside the block when the text is taller than the lane.
        className="flex-1 min-h-0 overflow-y-auto overscroll-contain cursor-text rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        data-testid={`block-edit-${block.id}`}
      >
        {block.character && (
          <p className="text-xs font-bold text-foreground/90 mt-0.5">
            {block.character}
            {block.emotion && (
              <span className="text-xxs italic text-foreground/50 ml-1">
                {block.emotion}
              </span>
            )}
          </p>
        )}
        {/* Item 7: body uses RichTextBox in read-only (disabled) mode so
            @character mentions render as chips. RichTextBox accepts
            `@[[nodeId]]` markers in `initialContent` and renders them as
            named chips via the provided `canvasNodes`. */}
        {trimmedText ? (
          <div className="mt-0.5">
            <RichTextBox
              // The key ensures the editor is fully re-hydrated when the
              // block's text changes (e.g. after Save). RichTextBox uses
              // `initialContentHydratedRef` internally and only hydrates once.
              key={`${block.id}-${block.text.length}`}
              canvasNodes={canvasNodes}
              disabled
              initialContent={displayText}
              containerClassName="w-full bg-transparent border-0 px-0 py-0 nowheel"
              editorTestId={`block-view-body-${block.id}`}
              minHeight={16}
              // Effectively unbounded: the box is as tall as its text and the
              // block body above is the single scroller (no nested scroll).
              maxHeight={100_000}
              maxLength={0}
            />
          </div>
        ) : (
          <p
            className="text-xs font-medium text-foreground/40 italic leading-relaxed mt-0.5"
            data-testid={`block-view-body-${block.id}`}
          >
            {isSpeech ? t('scriptBlockView.noDialogueYet') : t('scriptBlockView.noDescriptionYet')}
          </p>
        )}
        {block.camera && (
          <p className="text-xxs mt-0.5">
            <span className="text-text-muted">{t('scriptBlockView.camera')}</span>{' '}
            <span className="text-foreground/70">{block.camera}</span>
          </p>
        )}
      </div>
    </div>
  )
})
